import {
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LazyLoadEvent, MenuItem } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { AppComponentBase, PagedTableSummary } from 'src/app/app-component-base';
import {
    DynamicCatalogCreateCommand,
    DynamicCatalogGetFormInfoQuery,
    DynamicCatalogServiceProxy,
    DynamicCatalogUpdateCommand,
    DataByIdQuery,
    DynamicCatalogFormInfoResponse,
    ComboboxItemDto,
    GetReferencedComboboxQuery
} from '../../../shared/service-proxies/service-proxies';
import { DateTimeService } from '../../../shared/services/datetime.service';
import { AppComponent } from '../../../app/app.component';
import { ChangeLogService } from '../../../app/_services/changelog.service';
import * as moment from 'moment';

@Component({
    selector: 'app-dynamiccatalog-form',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './dynamiccatalog-form.component.html',
    providers: [
        AppComponent
    ]
})
export class DynamicCatalogFormComponent extends AppComponentBase implements OnInit {

    // Parámetros necesarios para el componente
    @Input() tabla: string;
    @Input() id?: number = null;
    @Input() urlVista: string;
    @Input() permisos: any;
    @Input() esModal: boolean;

    @Output() afterSaved: EventEmitter<boolean> = new EventEmitter<boolean>();

    form: FormGroup;

    model: any;
    tituloForm: string;
    claveAyudaForm: string;
    infoForm: DynamicCatalogFormInfoResponse;
    fieldLabels: any[] = [];
    definicionesCaptura: DefinicionParaCaptura[] = [];

    saving = false;
    blockedDocument = false;
    constructor(
        injector: Injector,
        private formBuilder: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private service: DynamicCatalogServiceProxy,
        private changeLogService: ChangeLogService,
        private dateTimeService: DateTimeService,
        private app: AppComponent
    ) {
        super(injector);
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.form.controls;
    }

    ngOnInit() {
        const self = this;

        if (self.id) {
            self.tituloForm = self.l('Catalogos.' + self.tabla + '.Edicion.Titulo');
        } else {
            self.tituloForm = self.l('Catalogos.' + self.tabla + '.Nuevo.Titulo');
        }

        self.claveAyudaForm = 'CATALOGOS.' + self.tabla.toUpperCase() + '.FORM';

        self.infoForm = new DynamicCatalogFormInfoResponse();
        self.getDefiniciones();
    }

    prepararEtiquetas(): void {
        const self = this;

        for (let i = 0, len = self.infoForm.definitionsList.length; i < len; i++) {

            const c = self.infoForm.definitionsList[i];

            if (c.editableEnCaptura) {
                self.fieldLabels.push({ field: c.campo, label: self.l('Catalogos.' + self.tabla + '.' + c.campo + '.Form') });
            }
        }
    }

    prepareForm(): void {
        const self = this;

        for (let i = 0, len = self.definicionesCaptura.length; i < len; i++) {
            const o = self.definicionesCaptura[i];
            o.data = null;

            if (o.tipo === 'boolean') {
                o.data = true;
            }
        }
    }

    getReferencedCombo(controlForm: DefinicionParaCaptura): void {
        const self = this;

        const req = new GetReferencedComboboxQuery();
        req.table = self.tabla;
        req.field = controlForm.campo;

        self.blockedDocument = true;

        self.service.getReferencedCombobox(req)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                controlForm.listaRef = data;
            });
    }

    getLabel(field: string): string {
        const self = this;

        let resp = '';
        const ex = self.fieldLabels.find(m => m.field === field);
        if (ex) {
            resp = ex.label;
        }

        return resp;
    }

    getTipoControl(field: string): string {
        const self = this;

        // Se revisa si el campo indicado tiene definida una relación a otra tabla..
        // Si es así, entonces se regresa como resp='combo'
        const rel = self.infoForm.relationsList.find(m => m.campoRelacion === field);

        if (rel) {
            return 'combo';
        } else {

            const numbersUnformat: string[] = ['int', 'tinyint', 'smallint', 'bigint'];
            const numbersformat: string[] = ['decimal', 'float', 'money'];
            const booleans: string[] = ['bit'];
            const date: string[] = ['date'];
            const datetime: string[] = ['datetime'];

            let resp = 'text';
            const def = self.infoForm.definitionsList.find(m => m.campo === field);

            const isNumberUnformat = numbersUnformat.indexOf(def.tipo) >= 0;
            const isNumberformat = numbersformat.indexOf(def.tipo) >= 0;
            const isBoolean = booleans.indexOf(def.tipo) >= 0;
            const isDate = date.indexOf(def.tipo) >= 0;
            const isDateTime = datetime.indexOf(def.tipo) >= 0;

            // Es número
            if (isNumberUnformat) {
                resp = 'numberU';
            } else if (isNumberformat) {
                resp = 'numberF';
            } else if (isBoolean) {
                resp = 'boolean';
            } else if (isDate) {
                // Hay que mostrar un control "calendar"
                resp = 'date';
            } else if (isDateTime) {
                resp = 'datetime';
            } else {
                resp = 'text';
            }

            return resp;
        }
    }

    getValorMaximo(field: string): number {
        const self = this;

        const numbers: string[] = ['int', 'tinyint', 'smallint', 'bigint', 'decimal', 'float', 'money'];

        let valorMaximo = 0;
        const def = self.infoForm.definitionsList.find(m => m.campo === field);

        const isNumber = numbers.indexOf(def.tipo) >= 0;

        // Es número
        if (def.tipo === 'int') {
            valorMaximo = 2147483647;
        } else if (def.tipo === 'tinyint') {
            valorMaximo = 255;
        } else if (def.tipo === 'smallint') {
            valorMaximo = 32767;
        } else if (def.tipo === 'bigint') {
            valorMaximo = 9223372036854775807;
        } else if (def.tipo === 'decimal') {
            const enteros = (def.longitud - def.decimales) - 1;
            valorMaximo = Math.pow(10, enteros) - 1;
        }

        return valorMaximo;
    }

    getEsRequerido(field: string): boolean {
        const self = this;

        const v = self.infoForm.validationsList.find(m => m.campo === field && m.regla === 1);
        return v !== null && v !== undefined;
    }

    getDefiniciones(): void {
        const self = this;

        const req = new DynamicCatalogGetFormInfoQuery();
        req.tabla = self.tabla;

        self.blockedDocument = true;

        self.service.getDynamicCatalogFormDefinitionsList(req)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.infoForm = data;

                // Preparar las etiquetas del formulario
                self.prepararEtiquetas();

                // Preparar las definiciones para captura
                self.preparaDefinicionesCaptura();

                // Preparar el formulario de acuerdo a las deifiniciones para captura obtenidas
                self.prepareForm();

                // Una vez traídas las definiciones, debemos ir por la lista de registros del catálogos
                if (self.id) {
                    self.getForEdit();
                }
            });
    }

    getForEdit(): void {
        const self = this;
        const query = new DataByIdQuery();

        query.tabla = self.tabla;
        query.id = self.id.toString();

        self.blockedDocument = true;

        self.service.getById(query)
            .pipe(finalize(() => {
                self.blockedDocument = false;
            }))
            .subscribe(data => {
                self.model = data;
                self.setearDatosEnDefinicionesCaptura();
            });
    }

    preparaDefinicionesCaptura(): void {
        const self = this;

        for (let i = 0, len = self.infoForm.definitionsList.length; i < len; i++) {
            const def = self.infoForm.definitionsList[i];
            if (def.editableEnCaptura) {

                const defCaptura = new DefinicionParaCaptura();
                defCaptura.campo = def.campo;
                defCaptura.etiqueta = self.getLabel(def.campo);
                defCaptura.tipo = self.getTipoControl(def.campo);
                defCaptura.valorMaximo = self.getValorMaximo(def.campo);
                defCaptura.tipoSQl = def.tipo;
                defCaptura.decimales = def.decimales;
                defCaptura.esRequerido = self.getEsRequerido(def.campo);
                defCaptura.longitud = def.longitud;

                if (defCaptura.tipo === 'combo') {
                    self.getReferencedCombo(defCaptura);
                }

                self.definicionesCaptura.push(defCaptura);
            }
        }
    }

    setearDatosEnDefinicionesCaptura(): void {
        const self = this;

        for (let i = 0, len = self.definicionesCaptura.length; i < len; i++) {
            const def = self.definicionesCaptura[i];

            if (def.tipo === 'date') {
                def.data = self.dateTimeService.getDateToAssignInFormControl(moment(self.model[def.campo]));
            } else if (def.tipo === 'datetime') {
                def.data = self.dateTimeService.getDateTimeToAssignInFormControl(moment(self.model[def.campo]));
            } else if (def.tipo === 'combo') {
                const fV = self.model[def.campo];
                if (fV) {
                    def.data = fV.toString();
                }
            } else {
                def.data = self.model[def.campo];
            }
        }
    }

    save(): void {
        const self = this;

        const validado = self.validarDatos();
        if (!validado) {
            self.afterSaved.emit(null);
            return;
        }

        const data = {};
        for (let i = 0, len = self.definicionesCaptura.length; i < len; i++) {
            const def = self.definicionesCaptura[i];

            data[def.campo] = def.data; // ? def.data : '';
        }

        if (self.id) {
            data['Id'] = self.id;
        }

        let suscribe = false;

        if (self.id) {
            const updateCmd = new DynamicCatalogUpdateCommand();

            updateCmd.tabla = self.tabla;
            updateCmd.data = data;

            self.blockedDocument = true;

            self.service.update(updateCmd)
                .pipe(finalize(() => {
                    self.blockedDocument = false;

                    // Si la varible suscribe = false, quiere decir que el servicio marcó un error
                    // aquí se emitirá el evento pasándole como paráemtro el valor "false",
                    // indicando que hubo un error
                    if (!suscribe) {
                        self.afterSaved.emit(false);
                    }
                }))
                .subscribe(result => {

                    suscribe = true;

                    self.notify.success(self.l('SavedSuccessfully'), self.l('Success'));
                    if (self.esModal) {
                        self.afterSaved.emit(true);
                    } else {
                        self.return();
                    }
                });
        } else {
            const createCmd = new DynamicCatalogCreateCommand();
            createCmd.tabla = self.tabla;
            createCmd.data = data;

            self.blockedDocument = true;

            self.service.create(createCmd)
                .pipe(finalize(() => {
                    self.blockedDocument = false;

                    // Si la varible suscribe = false, quiere decir que el servicio marcó un error
                    // aquí se emitirá el evento pasándole como paráemtro el valor "false",
                    // indicando que hubo un error
                    if (!suscribe) {
                        self.afterSaved.emit(false);
                    }
                }))
                .subscribe(result => {

                    suscribe = true;

                    self.notify.success(self.l('SavedSuccessfully'), self.l('Success'));
                    self.prepareForm();
                    self.afterSaved.emit(true);
                });
        }
    }

    validarDatos(): boolean {
        const self = this;

        const errores = [];

        for (let i = 0, len = self.definicionesCaptura.length; i < len; i++) {
            const def = self.definicionesCaptura[i];

            const labelField = self.l('Catalogos.' + self.tabla + '.' + def.campo + '.Form');

            // Si hay un valor capturado
            if (def.data) {

                // Si el tipo de numberU o numberF, se debe validar el valor capturado
                if (def.tipo === 'numberU' || def.tipo === 'numberF') {
                    const vMax = self.getValorMaximo(def.campo);
                    if (def.data > vMax) {
                        errores.push(self.l('DynamicsCatalogs.MaxValue', labelField, vMax));
                    }
                }
            }
        }

        if (errores.length > 0) {
            const msg = errores.join('<br/>');
            self.alertService.error(msg);
        }

        return errores.length <= 0;
    }

    showChangeHistory(): void {
        const self = this;

        self.changeLogService.open(self.tabla, self.id);
    }

    return(): void {
        this.router.navigate([this.urlVista]);
    }
}

export class DefinicionParaCaptura {
    campo: string;
    etiqueta: string;
    tipo: string; // combo, número, boolean, texto, date, datetime
    tipoSQl: string;
    longitud: number;
    decimales: number;
    esRequerido: boolean;
    valorMaximo: number;
    data: any;
    listaRef: ComboboxItemDto[];
}
